import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Pie } from 'react-chartjs-2';
import { Table, Row, Col, Card } from 'antd';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'antd/dist/reset.css';

// Đăng ký Chart.js + plugin hiển thị label (ChartDataLabels)
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Hàm tạo dữ liệu hợp đồng mẫu
function generateSampleContracts() {
  const contracts = [];
  for (let i = 1; i <= 50; i++) {
    const totalDebt = Math.floor(Math.random() * 10000000) + 100000;
    const remainingDebt = Math.floor(Math.random() * totalDebt);
    const paid = totalDebt - remainingDebt;
    contracts.push({
      ma_hop_dong: 98000 + i,
      don_vi: `CNKT${Math.floor(Math.random() * 3) + 1}`,
      ky_phat_sinh: `2025-${(i % 12) + 1}-01`,
      dich_vu: i % 2 === 0 ? 'VLC' : 'VLK',
      ma_nhan_vien: `NV${i}`,
      tuyen_sale: `Sale ${Math.floor(Math.random() * 5) + 1}`,
      ma_khach_hang_evtp: `KH${i}`,
      ten_don_vi: `Đơn vị ${i}`,
      tong_cong_no: totalDebt,
      phan_loai: i % 2 === 0 ? 'Công nợ dài hạn' : 'Công nợ ngắn hạn',
      thoi_han: `${30 + Math.floor(Math.random() * 90)} ngày`,
      cong_no_con_lai: remainingDebt,
      da_thanh_toan: paid,
    });
  }
  return contracts;
}

// Tổng hợp dữ liệu cho Dashboard
function generateSampleDashboardData(contracts) {
  const totalContracts = contracts.length;
  const totalDebt = contracts.reduce((acc, c) => acc + c.tong_cong_no, 0);
  const totalRemainingDebt = contracts.reduce((acc, c) => acc + c.cong_no_con_lai, 0);
  const totalPaid = contracts.reduce((acc, c) => acc + c.da_thanh_toan, 0);

  // Công nợ theo dịch vụ
  const debtByService = contracts.reduce((acc, c) => {
    acc[c.dich_vu] = (acc[c.dich_vu] || 0) + c.tong_cong_no;
    return acc;
  }, {});

  // Công nợ theo khách hàng
  const debtByCustomer = contracts.reduce((acc, c) => {
    if (!acc[c.ma_khach_hang_evtp]) {
      acc[c.ma_khach_hang_evtp] = { paid: 0, remaining: 0, total: 0 };
    }
    acc[c.ma_khach_hang_evtp].paid += c.da_thanh_toan;
    acc[c.ma_khach_hang_evtp].remaining += c.cong_no_con_lai;
    acc[c.ma_khach_hang_evtp].total += c.tong_cong_no;
    return acc;
  }, {});

  // Sắp xếp khách hàng theo tổng công nợ (giảm dần)
  const sortedCustomers = Object.entries(debtByCustomer)
    .sort((a, b) => b[1].total - a[1].total)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  // Top 5 hợp đồng nợ cao nhất
  const top5Contracts = [...contracts]
    .sort((a, b) => b.tong_cong_no - a.tong_cong_no)
    .slice(0, 5);

  // Tỷ lệ thanh toán (mô phỏng)
  const paidDay = Math.floor(totalDebt * 0.1);
  const paidWeek = Math.floor(totalDebt * 0.3);
  const paidMonth = Math.floor(totalDebt * 0.6);

  const ratioDay = ((paidDay / totalDebt) * 100).toFixed(2);
  const ratioWeek = ((paidWeek / totalDebt) * 100).toFixed(2);
  const ratioMonth = ((paidMonth / totalDebt) * 100).toFixed(2);

  // Tính nợ dài hạn vs. ngắn hạn
  let longTerm = 0;
  let shortTerm = 0;
  for (let c of contracts) {
    if (c.phan_loai === 'Công nợ dài hạn') {
      longTerm += c.tong_cong_no;
    } else {
      shortTerm += c.tong_cong_no;
    }
  }

  return {
    total_contracts: totalContracts,
    total_debt: totalDebt,
    total_remaining_debt: totalRemainingDebt,
    total_paid: totalPaid,
    debt_by_service: debtByService,
    debt_by_customer: sortedCustomers,
    top_5_contracts: top5Contracts,
    paid_day: paidDay,
    paid_week: paidWeek,
    paid_month: paidMonth,
    ratio_day: ratioDay,
    ratio_week: ratioWeek,
    ratio_month: ratioMonth,
    debt_by_type: {
      longTerm,
      shortTerm,
    },
  };
}

function TongQuanCongNoComponent() {
  const [contracts, setContracts] = useState([]);
  const [dashboardData, setDashboardData] = useState({});

  useEffect(() => {
    const sampleContracts = generateSampleContracts();
    setContracts(sampleContracts);
    setDashboardData(generateSampleDashboardData(sampleContracts));
  }, []);

  // ---- (1) Khai báo chiều cao cho Card bên trái (mỗi card) và tính chiều cao Card bên phải
  const leftCardHeight = 300; // Tổng chiều cao Card "Công nợ theo dịch vụ" hoặc "Phân loại nợ"
  const marginBetween = 16;  // Khoảng cách giữa 2 Card Pie
  const rightCardHeight = leftCardHeight * 2 + marginBetween; // Card "Công nợ theo khách hàng"

  // Định nghĩa style cho container bọc biểu đồ (không thay đổi Card)
  const chartWrapperStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  // Định nghĩa style cho phần scale
  const chartScaleStyle = {
    transform: 'scale(1)',        // phóng to 110%
    transformOrigin: 'center',      // tâm phóng là giữa biểu đồ
  };

  // (2) Options hiển thị % cho PieChart
  const pieOptions = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        formatter: (value, ctx) => {
          const dataset = ctx.chart.data.datasets[0];
          const total = dataset.data.reduce((acc, val) => acc + val, 0);
          return ((value / total) * 100).toFixed(2) + '%';
        },
        color: '#fff',
        font: {
          weight: 'bold',
          size: 12,
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            const dataset = tooltipItem.dataset;
            const total = dataset.data.reduce((acc, val) => acc + val, 0);
            const currentValue = dataset.data[tooltipItem.dataIndex];
            const percentage = ((currentValue / total) * 100).toFixed(2) + '%';
            return ` ${tooltipItem.label}: ${currentValue.toLocaleString()} (${percentage})`;
          },
        },
      },
    },
  };

  // (3) Biểu đồ Pie “Công nợ theo dịch vụ”
  const pieDataService = {
    labels: Object.keys(dashboardData.debt_by_service || {}),
    datasets: [
      {
        label: 'Công nợ theo dịch vụ',
        data: Object.values(dashboardData.debt_by_service || {}),
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
      },
    ],
  };

  // (4) Biểu đồ Pie “Phân loại nợ”
  const pieDataType = {
    labels: ['Công nợ dài hạn', 'Công nợ ngắn hạn'],
    datasets: [
      {
        data: [
          dashboardData.debt_by_type?.longTerm || 0,
          dashboardData.debt_by_type?.shortTerm || 0,
        ],
        backgroundColor: ['#8E44AD', '#F39C12'],
      },
    ],
  };

  // (5) Biểu đồ Bar “Công nợ theo khách hàng” (tắt data labels để bớt rối)
  const customerLabels = Object.keys(dashboardData.debt_by_customer || {});
  const paidData = customerLabels.map((key) => dashboardData.debt_by_customer[key]?.paid || 0);
  const remainingData = customerLabels.map((key) => dashboardData.debt_by_customer[key]?.remaining || 0);

  const barDataCustomer = {
    labels: customerLabels,
    datasets: [
      {
        label: 'Đã thanh toán',
        data: paidData,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        stack: 'Stack 0',
      },
      {
        label: 'Công nợ còn lại',
        data: remainingData,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        stack: 'Stack 0',
      },
    ],
  };
  const barOptions = {
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
    plugins: {
      datalabels: {
        display: false, // Tắt data labels trên cột
      },
    },
  };

  // (6) Cấu hình bảng (Table) Ant Design
  const columns = [
    {
      title: <strong>Mã HĐ</strong>,
      dataIndex: 'ma_hop_dong',
      key: 'ma_hop_dong',
    },
    {
      title: <strong>Tên đơn vị</strong>,
      dataIndex: 'ten_don_vi',
      key: 'ten_don_vi',
    },
    {
      title: <strong>Dịch vụ</strong>,
      dataIndex: 'dich_vu',
      key: 'dich_vu',
    },
    {
      title: <strong>Tổng công nợ</strong>,
      dataIndex: 'tong_cong_no',
      key: 'tong_cong_no',
      render: (value) => value?.toLocaleString('en-US') + ' VND',
    },
    {
      title: <strong>Công nợ còn lại</strong>,
      dataIndex: 'cong_no_con_lai',
      key: 'cong_no_con_lai',
      render: (value) => value?.toLocaleString('en-US') + ' VND',
    },
  ];

  // (7) Style chung cho Card
  const cardStyle = {
    border: '1px solid #000',
    borderRadius: '8px',
    marginBottom: '16px',
  };

  return (
    <div
      className="container mt-4"
      style={{
        maxWidth: '100%',
        margin: '0 auto',
        padding: '3%',
        textAlign: 'left',
      }}
    >
      {/* Tiêu đề trung tâm */}
      <h1 className="text-center mb-4">Dashboard Quản lý Công nợ</h1>

      {/* Thông tin nhanh về công nợ */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle}>
            <strong style={{ fontSize: '16px' }}>Tổng số HĐ</strong>
            <div style={{ fontSize: '20px', marginTop: '8px' }}>
              {dashboardData.total_contracts || 0}
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle}>
            <strong style={{ fontSize: '16px' }}>Tổng công nợ</strong>
            <div style={{ fontSize: '20px', marginTop: '8px' }}>
              {(dashboardData.total_debt || 0).toLocaleString('en-US')} VND
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle}>
            <strong style={{ fontSize: '16px' }}>Nợ còn lại</strong>
            <div style={{ fontSize: '20px', marginTop: '8px' }}>
              {(dashboardData.total_remaining_debt || 0).toLocaleString('en-US')} VND
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
              Tỷ lệ thanh toán
            </div>
            <div>
              <strong>Tỷ lệ thanh toán (Ngày):</strong>{' '}
              {dashboardData.ratio_day || 0}%
            </div>
            <div>
              <strong>Tỷ lệ thanh toán (Tuần):</strong>{' '}
              {dashboardData.ratio_week || 0}%
            </div>
            <div>
              <strong>Tỷ lệ thanh toán (Tháng):</strong>{' '}
              {dashboardData.ratio_month || 0}%
            </div>
          </Card>
        </Col>
      </Row>

      {/* Biểu đồ */}
      <Row gutter={[16, 16]} className="mt-4">
        {/* Cột bên trái (1/3) - 2 Card xếp dọc, mỗi card 300px */}
        <Col md={8}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Card
                title={<strong>Công nợ theo dịch vụ</strong>}
                style={{ ...cardStyle, height: `${leftCardHeight}px` }}
              >
                <div style={chartWrapperStyle}>
                  <div style={chartScaleStyle}>
                    <Pie data={pieDataService} options={pieOptions} />
                  </div>
                </div>
              </Card>
            </Col>
            <Col span={24}>
              <Card
                title={<strong>Phân loại nợ</strong>}
                style={{ ...cardStyle, height: `${leftCardHeight}px` }}
              >
                <div style={chartWrapperStyle}>
                  <div style={chartScaleStyle}>
                    <Pie data={pieDataType} options={pieOptions} />
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Cột bên phải (2/3) - Card cao = 2 * leftCardHeight + 16 */}
        <Col md={16}>
          <Card
            title={<strong>Công nợ theo khách hàng</strong>}
            style={{
              ...cardStyle,
              // Height = 616px
              height: `${rightCardHeight}px`,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Chart chiếm hết phần còn lại bên dưới header */}
            <div style={{ width: '100%', height: '500px', position: 'relative' }}>
              <Bar data={barDataCustomer} options={barOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Top 5 hợp đồng nợ cao nhất */}
      <Card title={<strong>Top 5 Hợp đồng nợ cao nhất</strong>} style={cardStyle}>
        <Table
          columns={columns}
          dataSource={dashboardData.top_5_contracts || []}
          rowKey="ma_hop_dong"
          pagination={false}
        />
      </Card>
    </div>
  );
}

export default TongQuanCongNoComponent;
